// @ts-check
import { Logger } from "../utils/Logger.js";

/**
 * API-based service for persisting markups using building model comment API
 * This service replaces the localStorage-based MarkupPersistenceService
 */
export class MarkupApiPersistenceService {
	constructor(fileId, apiActions) {
		this.logger = Logger.createLogger("MarkupApiPersistenceService");
		this.fileId = fileId;
		this.apiActions = apiActions;

		// Track which markers have been persisted to avoid duplicates
		this.persistedMarkerIds = new Set();

		// Validate required API actions
		const requiredActions = [
			"getBuildingModelComments",
			"createBuildingModelComment",
			"replyToBuildingModelComment",
		];
		for (const action of requiredActions) {
			if (!apiActions[action] || typeof apiActions[action] !== "function") {
				throw new Error(`Missing required API action: ${action}`);
			}
		}
	}

	/**
	 * Generate a unique identifier for view-specific markups
	 * @param {string} viewType - The view type (2D or 3D)
	 * @param {number} viewIndex - The view index (for 2D sheets)
	 * @returns {string} View identifier
	 */
	getViewIdentifier(viewType, viewIndex) {
		return `${viewType}_${viewIndex}`;
	}

	/**
	 * Save markups data using the building model comment API
	 * @param {string} viewerUrn - The URN identifier of the model
	 * @param {string} viewType - The view type (2D or 3D)
	 * @param {number} viewIndex - The view index (for 2D sheets)
	 * @param {Object} markupData - The markup data to save
	 * @returns {Promise<boolean>} Promise resolving to success status
	 */
	async saveMarkups(viewerUrn, viewType, viewIndex, markupData) {
		try {
			if (!markupData.markers || !Array.isArray(markupData.markers)) {
				this.logger.warn("No markers to save");
				return true;
			}

			// Filter out markers that have already been persisted
			const newMarkers = markupData.markers.filter(
				(marker) => !this.persistedMarkerIds.has(marker.id)
			);

			if (newMarkers.length === 0) {
				this.logger.warn("No new markers to save - all markers already saved");
				return true;
			}

			const savePromises = newMarkers.map(async (marker) => {
				const success = await this.createCommentForMarker(
					marker,
					viewType,
					viewIndex
				);
				if (success) {
					// Track this marker as persisted
					this.persistedMarkerIds.add(marker.id);
				}
				return success;
			});

			const results = await Promise.allSettled(savePromises);
			const failures = results.filter(
				(r) => r.status === "rejected" || r.value === false
			);

			if (failures.length > 0) {
				this.logger.error(
					`Failed to save ${failures.length} markers`,
					failures
				);
				return false;
			}

			return true;
		} catch (error) {
			return false;
		}
	}

	/**
	 * Load markups data using the building model comment API
	 * @param {string} viewerUrn - The URN identifier of the model
	 * @param {string} viewType - The view type (2D or 3D)
	 * @param {number} viewIndex - The view index (for 2D sheets)
	 * @returns {Promise<Object|null>} Promise resolving to markup data or null
	 */
	async loadMarkups(viewerUrn, viewType, viewIndex) {
		try {
			const viewIdentifier = this.getViewIdentifier(viewType, viewIndex);

			// Get all comments for this file
			const comments = await new Promise((resolve, reject) => {
				this.apiActions.getBuildingModelComments(this.fileId, (data) => {
					resolve(data || []);
				});
			});

			// Filter comments that belong to this specific view
			const viewComments = comments.filter((comment) => {
				const metadata = comment.metadata || {};
				return metadata.viewIdentifier === viewIdentifier;
			});

			if (viewComments.length === 0) {
				this.logger.warn(
					`No saved markups found for ${viewType} view (index: ${viewIndex})`
				);
				return null;
			}

			// Convert comments back to marker format
			const markers = viewComments.map((comment) =>
				this.convertCommentToMarker(comment)
			);

			// Track loaded markers as already persisted
			markers.forEach((marker) => {
				this.persistedMarkerIds.add(marker.id);
			});

			const markupData = {
				version: 1,
				counter: Math.max(...markers.map((m) => m.id)) + 1,
				markers,
				timestamp: Date.now(),
				lastSaved: viewComments[0]?.createdAt || new Date().toISOString(),
			};

			return markupData;
		} catch (error) {
			this.logger.error("Error loading markups", error);
			return null;
		}
	}

	/**
	 * Delete markups data using the building model comment API
	 * Note: The current API doesn't have a delete endpoint, so this logs a warning
	 * @param {string} viewerUrn - The URN identifier of the model
	 * @param {string} viewType - The view type (2D or 3D)
	 * @param {number} viewIndex - The view index (for 2D sheets)
	 * @returns {Promise<boolean>} Promise resolving to success status
	 */
	async deleteMarkups(viewerUrn, viewType, viewIndex) {
		try {
			this.logger.warn(
				`Delete markups requested for ${viewType} view (index: ${viewIndex}), but API doesn't support deletion. Comments will remain in the system.`
			);

			// Since we can't delete via API, we return true to allow the UI to clear local markers
			// The comments will remain in the database but won't be loaded for this view anymore
			// if they're properly filtered

			return true;
		} catch (error) {
			this.logger.error("Error deleting markups", error);
			return false;
		}
	}

	/**
	 * List all available markup sets for a model
	 * @param {string} viewerUrn - The URN identifier of the model
	 * @returns {Promise<Array>} Promise resolving to array of available markup sets
	 */
	async listMarkupSets(viewerUrn) {
		try {
			// Get all comments for this file
			const comments = await new Promise((resolve, reject) => {
				this.apiActions.getBuildingModelComments(this.fileId, (data) => {
					resolve(data || []);
				});
			});

			// Group comments by view and create summary
			const viewGroups = {};

			comments.forEach((comment) => {
				const metadata = comment.metadata || {};
				const viewIdentifier = metadata.viewIdentifier || "unknown";

				if (!viewGroups[viewIdentifier]) {
					viewGroups[viewIdentifier] = {
						viewType: metadata.viewType || "unknown",
						viewIndex: metadata.viewIndex || 0,
						comments: [],
						lastSaved: null,
					};
				}

				viewGroups[viewIdentifier].comments.push(comment);

				// Track the most recent comment timestamp
				const commentDate = comment.createdAt || comment.updatedAt;
				if (
					commentDate &&
					(!viewGroups[viewIdentifier].lastSaved ||
						commentDate > viewGroups[viewIdentifier].lastSaved)
				) {
					viewGroups[viewIdentifier].lastSaved = commentDate;
				}
			});

			// Convert to the expected format
			const results = Object.keys(viewGroups).map((viewIdentifier) => ({
				viewType: viewGroups[viewIdentifier].viewType,
				viewIndex: viewGroups[viewIdentifier].viewIndex,
				lastSaved: viewGroups[viewIdentifier].lastSaved,
				markerCount: viewGroups[viewIdentifier].comments.length,
			}));

			return results;
		} catch (error) {
			this.logger.error("Error listing markup sets", error);
			return [];
		}
	}

	/**
	 * Create a building model comment for a marker
	 * @param {Object} marker - Marker data
	 * @param {string} viewType - View type
	 * @param {number} viewIndex - View index
	 * @returns {Promise<boolean>} Success status
	 */
	async createCommentForMarker(marker, viewType, viewIndex) {
		try {
			const commentData = {
				comment: marker.comment || `Marker ${marker.id}`,
				metadata: {
					markerId: marker.id,
					dbId: marker.dbId,
					modelPoint: marker.modelPoint, // Save 2D marker coordinates
					model: marker.model, // Save model reference for 2D markers
					viewType,
					viewIndex,
					viewIdentifier: this.getViewIdentifier(viewType, viewIndex),
					cameraData: marker.cameraData,
					markerMetadata: marker.metadata,
					userName: marker.userName,
					userImageLink: marker.userImageLink, // Save the user's avatar/image link
					createdByMarkupExtension: true,
				},
			};

			return new Promise((resolve, reject) => {
				this.apiActions.createBuildingModelComment(
					this.fileId,
					commentData,
					() => {},
					true,
					(response) => {
						this.logger.debug(`Created comment for marker ${marker.id}`);

						// Create replies if marker has replies
						if (marker.replies && marker.replies.length > 0) {
							this.createRepliesForComment(response.id, marker.replies)
								.then(() => resolve(true))
								.catch(() => resolve(true)); // Don't fail the whole operation if replies fail
						} else {
							resolve(true);
						}
					},
					false // Don't refresh comments automatically
				);
			});
		} catch (error) {
			this.logger.error(
				`Error creating comment for marker ${marker.id}`,
				error
			);
			return false;
		}
	}

	/**
	 * Create replies for a comment
	 * @param {number} commentId - Comment ID
	 * @param {Array} replies - Reply data array
	 * @returns {Promise<boolean>} Success status
	 */
	async createRepliesForComment(commentId, replies) {
		try {
			const replyPromises = replies.map((reply) => {
				return new Promise((resolve, reject) => {
					this.apiActions.replyToBuildingModelComment(
						commentId,
						{ reply: reply.text || reply.comment || "" },
						null, // fileId not needed for replies
						() => resolve(true),
						false // Don't refresh comments
					);
				});
			});

			await Promise.all(replyPromises);
			return true;
		} catch (error) {
			this.logger.error(
				`Error creating replies for comment ${commentId}`,
				error
			);
			return false;
		}
	}

	/**
	 * Convert a building model comment back to marker format
	 * @param {Object} comment - Comment data from API
	 * @returns {Object} Marker data
	 */
	convertCommentToMarker(comment) {
		const metadata = comment.metadata || {};
		const user = comment.commentedByUser || {};

		// Extract user name - construct from API user data
		let userName =
			`${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown";

		// Extract user avatar - prioritize API user data, then metadata
		const userImageLink = user.imageLink || metadata.userImageLink || null;

		return {
			id: comment.id,
			dbId: metadata.dbId || null,
			modelPoint: metadata.modelPoint || null, // Restore 2D marker coordinates
			model: metadata.model || null, // Restore model reference for 2D markers
			metadata: metadata.markerMetadata || {},
			cameraData: comment.cameraData || metadata.cameraData || null,
			comment: comment.comment || "",
			userName: userName,
			userImageLink: userImageLink,
			replies:
				comment.responses?.map((reply) => ({
					comment: reply.comment || "",
					userName:
						reply.commentedByUser?.firstName +
							" " +
							reply.commentedByUser?.lastName || "Unknown",
					timestamp: reply.createdAt,
					createdAt: reply.createdAt,
					metadata: reply.metadata || {},
				})) || [],
		};
	}

	/**
	 * Clear the tracking of persisted markers (useful when switching views or clearing markers)
	 */
	clearPersistedMarkerTracking() {
		this.persistedMarkerIds.clear();
		this.logger.debug("Cleared persisted marker tracking");
	}

	/**
	 * Check if a marker has been persisted
	 * @param {number} markerId - Marker ID to check
	 * @returns {boolean} True if marker has been persisted
	 */
	isMarkerPersisted(markerId) {
		return this.persistedMarkerIds.has(markerId);
	}
}
